/**
 * Vendor layer — the only place `gtag` is spelled.
 *
 * Every sink is fail-soft: analytics must never throw into a product flow. Two debug lines are
 * deliberate, mirroring the app: one proves we *decided* to send, the other that the SDK *accepted*
 * it. When the console shows events and the dashboard does not, the gap between them is the answer.
 */
export interface AnalyticsSink {
  identify(id: string | null, traits?: Record<string, string>): void;
  track(event: string, props?: Record<string, unknown>): void;
  reset(): void;
  flush(): void;
}

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

/** GA4 accepts strings and numbers only; drop nulls, coerce the rest. Same rule as the app. */
export function cleanProps(props: Record<string, unknown>): Record<string, string | number> {
  const clean: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(props)) {
    if (v === null || v === undefined) continue;
    clean[k] = typeof v === 'number' ? v : String(v);
  }
  return clean;
}

export const noopSink: AnalyticsSink = {
  identify: () => undefined,
  track: () => undefined,
  reset: () => undefined,
  flush: () => undefined,
};

export const debugSink: AnalyticsSink = {
  identify: (id, traits) => console.debug('[analytics] identify', id, traits ?? {}),
  track: (event, props) => console.debug('[analytics]', event, cleanProps(props ?? {})),
  reset: () => console.debug('[analytics] reset'),
  flush: () => undefined,
};

/** Loads gtag.js once. `debug_mode` is on everywhere except the production host. */
export function loadGa4(measurementId: string): void {
  if (typeof document === 'undefined' || window.gtag) return;
  window.dataLayer = window.dataLayer || [];
  const gtag: Gtag = (...args: unknown[]) => {
    window.dataLayer!.push(args);
  };
  window.gtag = gtag;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(s);
  gtag('js', new Date());
  gtag('config', measurementId, { debug_mode: location.hostname !== 'facturita.ec' });
}

export function ga4Sink(measurementId: string): AnalyticsSink {
  return {
    identify(id, traits = {}) {
      try {
        // send_page_view: false — enhanced measurement already sends it; this call is only identity.
        window.gtag?.('config', measurementId, { user_id: id, send_page_view: false });
        if (Object.keys(traits).length > 0) window.gtag?.('set', 'user_properties', traits);
      } catch (e) {
        console.debug('[analytics] FAILED identify', e);
      }
    },
    track(event, props = {}) {
      try {
        const clean = cleanProps(props);
        window.gtag?.('event', event, clean);
        console.debug('[analytics] → GA4 ok:', event, clean);
      } catch (e) {
        console.debug('[analytics] FAILED track', event, e);
      }
    },
    reset() {
      try {
        // Without this the next account on this browser merges into the previous profile.
        window.gtag?.('set', { user_id: null });
      } catch {
        /* fail-soft */
      }
    },
    flush() {
      /* gtag already uses sendBeacon; nothing to force. */
    },
  };
}

export function fanOut(sinks: AnalyticsSink[]): AnalyticsSink {
  return {
    identify: (id, traits) => sinks.forEach((s) => s.identify(id, traits)),
    track: (event, props) => sinks.forEach((s) => s.track(event, props)),
    reset: () => sinks.forEach((s) => s.reset()),
    flush: () => sinks.forEach((s) => s.flush()),
  };
}
